import { useState } from 'react';
import Dialogo from '../../components/ui/Dialogo';
import { btnPrimario, btnSecundario } from '../../components/ui/estilos';
import { Campo, entrada } from '../agenda/campos';

export const ROLES = ['admin', 'mesero', 'cocina', 'cajero', 'almacenista'];
export const ROL_LABEL: Record<string, string> = { admin: 'Administrador', mesero: 'Mesero', cocina: 'Cocina', cajero: 'Cajero', almacenista: 'Almacenista' };
// Lo que se teclea para entrar: la parte antes del @ (el login acepta ambas).
export const usuarioDeAcceso = (email: string) => (email || '').split('@')[0];

function Botones({ onCerrar, ok, ocupado, disabled }: { onCerrar: () => void; ok: string; ocupado?: boolean; disabled?: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 10 }}>
      <button type="button" onClick={onCerrar} style={{ ...btnSecundario, flex: 1, height: 52 }}>Cancelar</button>
      <button type="submit" disabled={ocupado || disabled} style={{ ...btnPrimario, flex: 2, height: 52, fontSize: 16, opacity: ocupado || disabled ? .6 : 1 }}>{ok}</button>
    </div>
  );
}

// Crear, cambiar contraseña o borrar piden otra vez la del admin (el backend la valida en password_admin).
export function useClaveAdmin() {
  const [pide, setPide] = useState<null | { motivo: string; resolve: (c: string | null) => void }>(null);
  const [clave, setClave] = useState('');
  const pedir = (motivo: string) => new Promise<string | null>(resolve => { setClave(''); setPide({ motivo, resolve }); });
  const cerrar = (v: string | null) => { pide?.resolve(v); setPide(null); setClave(''); };
  const elemento = pide && (
    <Dialogo titulo="Confirma con tu contraseña" onCerrar={() => cerrar(null)} ancho={400}>
      <form onSubmit={e => { e.preventDefault(); if (clave) cerrar(clave); }} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: 15, color: 'var(--muted)' }}>{pide.motivo}. Escribe tu contraseña de administrador.</div>
        <input type="password" autoFocus autoComplete="current-password" value={clave} onChange={e => setClave(e.target.value)} placeholder="Tu contraseña" aria-label="Tu contraseña" style={entrada} />
        <Botones onCerrar={() => cerrar(null)} ok="Confirmar" disabled={!clave} />
      </form>
    </Dialogo>
  );
  return [pedir, elemento] as const;
}

function CampoClave({ valor, onCambio, label }: { valor: string; onCambio: (v: string) => void; label: string }) {
  const [ver, setVer] = useState(false);
  return (
    <Campo label={label}>
      <div style={{ display: 'flex', gap: 8 }}>
        <input type={ver ? 'text' : 'password'} value={valor} onChange={e => onCambio(e.target.value)} autoComplete="new-password" style={{ ...entrada, flex: 1 }} />
        {/* Nadie la puede consultar después: el ojo evita el error de dedo que deja a alguien fuera. */}
        <button type="button" onClick={() => setVer(v => !v)} style={{ ...btnSecundario, flex: 'none', fontSize: 14 }}>{ver ? 'Ocultar' : 'Ver'}</button>
      </div>
      <span style={{ fontSize: 13, color: 'var(--muted)' }}>Mínimo 6 caracteres. Anótala: después solo se puede cambiar, no consultar.</span>
    </Campo>
  );
}

export function DialogoNuevoEmpleado({ onCerrar, onCrear }: { onCerrar: () => void; onCrear: (datos: any) => Promise<string | null> }) {
  const [f, setF] = useState({ nombre: '', email: '', password: '', rol: 'mesero' });
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const set = (k: keyof typeof f, v: string) => setF(x => ({ ...x, [k]: v }));
  const crear = async () => {
    if (!f.nombre.trim() || !f.email.trim()) { setError('Nombre y correo son obligatorios.'); return; }
    if (f.password.trim().length < 6) { setError('La contraseña debe tener al menos 6 caracteres.'); return; }
    setOcupado(true);
    const err = await onCrear({ ...f, nombre: f.nombre.trim(), email: f.email.trim() });
    setOcupado(false);
    if (err) setError(err);
  };
  return (
    <Dialogo titulo="Nuevo empleado" onCerrar={onCerrar} ancho={460}>
      <form onSubmit={e => { e.preventDefault(); crear(); }} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Campo label="Nombre"><input autoFocus value={f.nombre} onChange={e => set('nombre', e.target.value)} maxLength={100} style={entrada} /></Campo>
        <Campo label="Correo">
          <input type="email" value={f.email} onChange={e => set('email', e.target.value)} style={entrada} />
          {f.email.includes('@') && <span style={{ fontSize: 13, color: 'var(--muted)' }}>Entrará tecleando <b>{usuarioDeAcceso(f.email)}</b></span>}
        </Campo>
        <CampoClave label="Contraseña" valor={f.password} onCambio={v => set('password', v)} />
        <Campo label="Rol">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {ROLES.map(r => <button key={r} type="button" onClick={() => set('rol', r)} aria-pressed={f.rol === r}
              style={{ ...btnSecundario, height: 44, fontSize: 15, borderColor: f.rol === r ? 'var(--brand-texto)' : undefined, background: f.rol === r ? 'var(--well)' : undefined }}>{ROL_LABEL[r]}</button>)}
          </div>
        </Campo>
        {error && <div role="alert" style={{ color: 'var(--danger)', fontWeight: 700 }}>{error}</div>}
        <Botones onCerrar={onCerrar} ok="Crear empleado" ocupado={ocupado} />
      </form>
    </Dialogo>
  );
}

export function DialogoContrasena({ nombre, onCerrar, onGuardar }: { nombre: string; onCerrar: () => void; onGuardar: (p: string) => Promise<string | null> }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const guardar = async () => {
    if (password.trim().length < 6) { setError('La contraseña debe tener al menos 6 caracteres.'); return; }
    const err = await onGuardar(password);
    if (err) setError(err);
  };
  return (
    <Dialogo titulo={`Nueva contraseña de ${nombre}`} onCerrar={onCerrar} ancho={440}>
      <form onSubmit={e => { e.preventDefault(); guardar(); }} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <CampoClave label="Contraseña nueva" valor={password} onCambio={setPassword} />
        {error && <div role="alert" style={{ color: 'var(--danger)', fontWeight: 700 }}>{error}</div>}
        <Botones onCerrar={onCerrar} ok="Cambiar contraseña" />
      </form>
    </Dialogo>
  );
}
